'use client'

import dynamic from 'next/dynamic'
import Link from 'next/link'
import { useId } from 'react'
import { Grid2 as Grid, Box, Typography } from '@mui/material'
import { KeyboardBackspaceRounded as KeyboardBackspaceRoundedIcon } from '@mui/icons-material'

// Types
import type { SlideshowItemProps } from '@/types/components/molecules/items/slideshow'

// Helpers
import { useTranslation } from '@/helpers/i18n/client'

// Components
const TriangleShapeAtom = dynamic(() => import('@/components/atoms/shapes/triangle'))
const ShadowButtonAtom = dynamic(() => import('@/components/atoms/buttons/text/shadow'))
const ArrowShapeAtom = dynamic(() => import('@/components/atoms/shapes/arrow'))

const SlideshowItemMolecule = (props: SlideshowItemProps) => {
	// Props
	const { title, subtitle, slogan, image, link } = props

	// Varaibles
	const { t } = useTranslation()
	const id = useId()

	return (
		<Grid container flexDirection="row-reverse" justifyContent="space-between">
			{image && (
				<Grid
					size={{ xs: 12, sm: 5 }}
					p={3}
					position="relative"
					bgcolor="primary.main"
					borderRadius={5}
					sx={{
						'& picture': {
							display: 'inline-flex',
							width: 1,
							height: { xs: 'calc(100dvw - 88px)', sm: 'calc(41.666666dvw - 76px)', lg: 432 },
							'& img': {
								width: 1,
								height: 1,
								objectFit: 'scale-down',
								objectPosition: 'center'
							}
						}
					}}
				>
					<picture>
						<img src={image} alt={t(title ?? subtitle ?? slogan ?? '')} />
					</picture>

					<svg style={{ width: 0, height: 0, position: 'absolute', overflow: 'hidden' }}>
						<defs>
							<filter id={id}>
								<feGaussianBlur in="SourceGraphic" stdDeviation="12" result="blur" />
								<feColorMatrix in="blur" mode="matrix" values="1 0 0 0 0 0 1 0 0 0 0 0 1 0 0 0 0 0 19 -9" result="goo" />
								<feComposite in="SourceGraphic" in2="goo" operator="atop" />
							</filter>
						</defs>
					</svg>

					<Box
						position="absolute"
						left="50%"
						bottom={0}
						zIndex={1}
						sx={{
							transform: 'translate(-50%, 100%)',
							filter: `url("#${id}")`
						}}
					>
						<Box width={200} height={50} bgcolor="common.white" />
						<Box width={150} height={30} position="absolute" top={-25} left="50%" bgcolor="common.white" sx={{ transform: 'translateX(-50%)' }} />
					</Box>
				</Grid>
			)}

			<Grid size={{ xs: 12, sm: 6.5, md: 7 }} mt={{ xs: 6, sm: 2, md: 5.25 }}>
				<Grid container justifyContent="center">
					<Grid size={{ xs: 12, md: 11, lg: 8.5, xl: 8 }}>
						<Grid container>
							{slogan && (
								<Grid size={12} textAlign={{ xs: 'center', sm: 'inherit' }}>
									<Typography
										component="span"
										width="auto"
										position="relative"
										fontSize={{ xs: 12, md: 14 }}
										fontWeight={600}
										lineHeight={1.5}
										letterSpacing={-1}
										textAlign={{ xs: 'center', md: 'left' }}
										color="primary.dark"
										sx={{
											'&::before': {
												content: '""',
												opacity: 0.1,
												width: 'calc(100% + 6px)',
												height: { xs: 11, md: 15 },
												position: 'absolute',
												left: { xs: -1, md: 3 },
												bottom: -1.5,
												bgcolor: 'primary.dark'
											}
										}}
									>
										{t(slogan)}
									</Typography>
								</Grid>
							)}

							{title && (
								<Grid size={12} mt={{ xs: 2.75, sm: 1 }}>
									<Typography
										variant="h2"
										position="relative"
										fontSize={{ xs: 30, md: 45, lg: 60 }}
										fontWeight={800}
										lineHeight={{ xs: 1.5, md: 1.2 }}
										letterSpacing={{ xs: -1.5, md: -4 }}
										textAlign="left"
										sx={{
											'& svg': {
												opacity: 0.3,
												width: '50%',
												height: 'auto',
												position: 'absolute',
												top: '50%',
												right: 0,
												transform: `translateY(-50%)`
											}
										}}
									>
										{t(title)}

										<TriangleShapeAtom />
									</Typography>
								</Grid>
							)}

							{subtitle && (
								<Grid size={{ xs: 9, sm: 10, md: 8, lg: 9 }} mt={{ xs: 1.625, sm: 1.5 }}>
									<Typography variant="subtitle1" component="p" width={1} fontSize={{ xs: 15, md: 18, lg: 21 }} fontWeight={400} lineHeight={1.5} color="#767676">
										{t(subtitle)}
									</Typography>
								</Grid>
							)}

							{link && (
								<Grid
									size={{ xs: 11, sm: 12, md: 8.5, lg: 9.5 }}
									position="relative"
									mt={{ xs: 1.75, sm: 2.5 }}
									sx={{
										'& > svg': {
											position: 'absolute',
											right: 0,
											bottom: 0,
											opacity: 0.2,
											transform: 'rotate(-14deg)'
										}
									}}
								>
									<Link href={link}>
										<ShadowButtonAtom title="form:button.products" endIcon={<KeyboardBackspaceRoundedIcon />} />
									</Link>

									<ArrowShapeAtom color="#1c242c" />
								</Grid>
							)}
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	)
}

export default SlideshowItemMolecule
